import { css } from '@lastbrand/web-ui-styled-system/css'
import { useTranslation } from 'next-i18next'

import type { PLTypedWidgetProps } from '@src/components/pl-base-widget'
import {
  toAccordionItems,
  toAirlineCompatibilitySection,
  toDetailsTabTracker,
} from '@src/components/widgets/accordion/accordion.adapter'
import type { AccordionItemsProp } from '@src/components/widgets/accordion/accordion.adapter'
import { TEST_LOCATORS } from '@src/constants/test-locators'
import AirlineCompatibility from '@src/quince-components/src/atoms/airlineCompability/AirlineCompability'
import { AIRLINE_COMPATIBILITY_HASH } from '@src/quince-components/src/atoms/productForm/product-form-shared'
import useHash from '@src/quince-components/src/hooks/useHash'
import { ProductDetailsTabWithReadMore } from '@src/quince-site-pdp/src/organisms/productPageSections/product-details-tabs'
import { DETAILS_AND_CARE_TRACKING_META_DATA } from '@src/quince-site-pdp/src/organisms/productPageSections/productDetailsAndCare/productDetailsAndCare.helper'

import type { AccordionWidgetData } from '@lastbrand/storefront-proto-registry/api/v1/widget'

const tabWrapperCss = css({
  // Figma PDP form column: space-24 above the accordion's top divider on desktop. Below `lg` this
  // carries the PL-only half of the buy-box→accordion gap: the shared `ProductDescription` wrapper
  // keeps its space-20 (it also serves non-PL) and this adds the other 20px, so PL still lands on the
  // spec 40px without moving the non-PL surface.
  mt: { lg: 'space-24' },
  mx: { base: 'auto', lg: '0' },
  mb: '0',
  // The accordion→BRAND_COMPARISON gap lives on BRAND_COMPARISON's `mt`, not here — mirroring non-PL
  // (`product-details-and-care-ds` pb 0 + `brandComparisonDsClass` mt space-24). Both widgets can
  // render null, but only this one leaves a *shell* behind when it does (AccordionWidget has no
  // `isEmpty`, so `render-widget.tsx` still emits a zero-height `[data-widget-type="ACCORDION"]` div);
  // a `pb` here would vanish with the shell's children and collapse the seam to 0.
  pb: '0',
  width: '100%',
  // Figma PDP details column: items cap at 480px on desktop; mobile stays fluid.
  maxWidth: { lg: '480px' },
})

export function AccordionWidgetComponentDS(props: PLTypedWidgetProps<AccordionWidgetData> & AccordionItemsProp) {
  // Rows are resolved by the widget shell (`index.tsx`) so the HTML-children vs SVD-attribute choice is made
  // once; fall back to flattening the children here for any direct render. `Widget.widgets` is a recursive
  // proto type that can't be read cleanly through `Omit<Widget,'data'>`, so read the nested HTML children via
  // a record cast (mirrors the FAQ widget); the adapter narrows them.
  const propsRecord = props as unknown as Record<string, unknown>
  const items = props.items ?? toAccordionItems(propsRecord.widgets)
  const { t } = useTranslation('common')

  // Airline compatibility rides on the ACCORDION's own data; the DETAILS `#airline-compatibility` link
  // opens the legacy modal via the hash (below).
  const airlineSection = toAirlineCompatibilitySection(props.data.accordionData)

  const [hash] = useHash()
  const showAirlineCompatibility = hash === AIRLINE_COMPATIBILITY_HASH

  // Clear the hash so the modal can be reopened — mirrors the non-PL close in
  // `use-product-form.tsx`'s `hideAirlineCompatbility` byte-for-byte. Deliberately not using
  // `router.replace`/`router.push` from the next router: `router.replace(pathname)` triggers a
  // route transition (scroll reset + re-render), and `router.push({ hash: '' })` still pushes a
  // trailing `#` into history. `history.replaceState` + a manual `hashchange` dispatch is a
  // scoped no-navigation update that just resets the hash and notifies `useHash` — matching
  // the non-PL behavior exactly.
  const hideAirlineCompatibility = () => {
    if (typeof window === 'undefined') return
    const url = new URL(window.location.href)
    url.hash = ''
    window.history.replaceState(null, '', url.toString())
    // eslint-disable-next-line i18next/no-literal-string
    window.dispatchEvent(new HashChangeEvent('hashchange'))
  }

  if (items.length === 0) return null

  return (
    <>
      <div className={tabWrapperCss} data-testid={TEST_LOCATORS.PDP_SECTIONS.DETAILS_AND_CARE}>
        {items.map((item, index) => (
          <ProductDetailsTabWithReadMore
            key={item.id}
            tabId={item.id}
            // The same container identity `product-details-and-care-ds` sends, so the DS tab click names
            // its container on PL and non-PL alike. Legacy mode still sends no container context.
            trackingMetaData={DETAILS_AND_CARE_TRACKING_META_DATA}
            // A real per-row tracker so ProductDetailsAndCareV2 fires `click__tabs__tab` on expand — it
            // no-ops on an empty tracker (the prior `tracker: ''`).
            tab={{
              id: item.id,
              title: item.title,
              content: item.html,
              // SVD rows carry a pre-resolved tracker (their tab type is known from the attribute key, so no
              // locale-dependent title lookup is needed); HTML-child rows resolve it as before.
              tracker: item.tracker ?? toDetailsTabTracker(item.title, item.id),
            }}
            textContent={item.html}
            isFirstTab={index === 0}
            // Only the "More Information" row opens by default (21+ disclosure on alcohol PDPs, STRFRNT-16822);
            // proto `defaultExpanded` is deliberately ignored — the BE sends the wrong set (STRFRNT-15816).
            initiallyOpen={item.title.trim().toLowerCase() === t('more_information').trim().toLowerCase()}
            // Suppresses the first row's top border on MOBILE — the shared row only draws it when
            // `!isReviewHighlightsSectionDisplayed || !isMobile`. On mweb we don't want that divider;
            // desktop keeps it via the component's `!isMobile` branch.
            isReviewHighlightsSectionDisplayed={true}
          />
        ))}
      </div>
      {airlineSection && (
        <AirlineCompatibility
          show={showAirlineCompatibility}
          onCloseAirlineCompatbility={hideAirlineCompatibility}
          airlineCompatibilityListingCS={[airlineSection]}
        />
      )}
    </>
  )
}
